import type { TFunction } from "i18next";
import { COMPARISON_PERIOD_DATE_PATTERN, VALUE_FORMAT_TYPE } from "@/constants";
import { parseDateValue } from "@/shared/lib/datePattern";
import { formatDateValue } from "@/shared/lib/valueFormat";

/**
 * What a widget bound to a comparison query shows about it: the two periods it compared.
 *
 * They ride on the widget's data item and on a query preview, and are display only. A comparison's
 * formula columns carry no format of their own: each widget formats them like any other column.
 */

/** One period as ISO dates, both ends inclusive. */
export type AppComparisonPeriod = { start: string; end: string };

/** The period a widget shows and the one it is compared with, as resolved for this run. */
export type AppComparisonPeriods = {
  current: AppComparisonPeriod;
  previous: AppComparisonPeriod;
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function isoDateOf(raw: unknown): string | null {
  return typeof raw === "string" && ISO_DATE.test(raw) && parseDateValue(raw) !== null
    ? raw
    : null;
}

function periodOf(raw: unknown): AppComparisonPeriod | null {
  if (typeof raw !== "object" || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const start = isoDateOf(record.start);
  const end = isoDateOf(record.end);
  return start !== null && end !== null ? { start, end } : null;
}

/**
 * A data item's or preview's `comparison_periods`, read defensively: null when absent -- a widget
 * on a plain query, or a comparison with another query rather than a period -- or malformed.
 */
export function comparisonPeriodsOf(raw: unknown): AppComparisonPeriods | null {
  if (typeof raw !== "object" || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const current = periodOf(record.current);
  const previous = periodOf(record.previous);
  return current !== null && previous !== null ? { current, previous } : null;
}

function periodText(t: TFunction<"appStudio">, period: AppComparisonPeriod, pattern: string) {
  const format = { type: VALUE_FORMAT_TYPE.DATE, output_pattern: pattern };
  const start = formatDateValue(period.start, format) ?? period.start;
  if (period.start === period.end) {
    return start;
  }
  const end = formatDateValue(period.end, format) ?? period.end;
  return t("comparisonPeriods.range", { start, end });
}

const yearOf = (isoDate: string) => isoDate.slice(0, 4);

/**
 * Both periods as a widget captions them -- `Nov 1 – Nov 7` and `Oct 25 – Oct 31`, a single day as
 * one date. Dated with the year only when the two fall in different years, since "same period last
 * year" would otherwise print exactly the dates of this one.
 */
export function comparisonPeriodTexts(
  t: TFunction<"appStudio">,
  periods: AppComparisonPeriods,
): { current: string; previous: string } {
  const spansYears =
    yearOf(periods.current.start) !== yearOf(periods.previous.start) ||
    yearOf(periods.current.end) !== yearOf(periods.previous.end);
  const pattern = spansYears
    ? COMPARISON_PERIOD_DATE_PATTERN.WITH_YEAR
    : COMPARISON_PERIOD_DATE_PATTERN.SAME_YEAR;
  return {
    current: periodText(t, periods.current, pattern),
    previous: periodText(t, periods.previous, pattern),
  };
}
